/**
 * HRProviders — apprenticeship learning providers (tables 0072/0073) with the
 * RoATP standing indicator, a create/edit drawer, and the delivered-standards
 * sub-list. Modelled on HRCandidates; colors/sizes flow from theme/tokens.ts.
 *
 * Honesty rules this page carries:
 *  - roatpExpiry ABSENT = "Unverified" — its own state, never a green tick.
 *    Absence of a date is NOT good standing (same principle as a blank salary
 *    showing "unknown", not £0).
 *  - RoATP standing is "as last recorded" — there is no live RoATP feed, and
 *    the indicator's tooltip says so; a neutral state must not read as
 *    verified-current.
 *  - Provider update is partial (empty = unchanged): a recorded expiry CANNOT
 *    be cleared back to unverified from this form. No fake clear affordance —
 *    clearing awaits OptionalString adoption on roatp_expiry (stage-1
 *    follow-up).
 *  - Lifecycle is a status flip (active/inactive) — no delete button anywhere.
 */
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Plus } from 'lucide-react'
import { SectionTitle, LoadingState, ErrorState, EmptyState, Toggle } from '../../DashboardShared'
import {
  usePolledAsync, listProviders, createProvider, updateProvider, setProviderStatus,
  listStandards, addStandard, deleteStandard,
} from '../../api'
import type { LearningProvider, LearningProviderStandard } from '../../types'
import { ApiError, apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { HRMetric } from './HROverview'
import { ink, neutral, overlay, sensitive, success, surface } from '../../../../theme/tokens'

// ── RoATP indicator — a pure function of roatpExpiry against today ─────────────

export type RoatpKind = 'lapsed' | 'expiring' | 'valid' | 'unverified'

export interface RoatpIndicator {
  kind: RoatpKind
  label: string
  /** en-GB formatted expiry date, absent when unverified. */
  dateLabel?: string
}

export function roatpIndicator(expiry?: string, now: Date = new Date()): RoatpIndicator {
  // No recorded expiry = UNVERIFIED — an honest state of its own, never
  // implied validity.
  if (!expiry) return { kind: 'unverified', label: 'Unverified' }
  const d = new Date(expiry)
  if (Number.isNaN(d.getTime())) return { kind: 'unverified', label: 'Unverified' }
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const expiryDay = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const days = Math.round((expiryDay.getTime() - today.getTime()) / 86_400_000)
  const dateLabel = expiryDay.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  if (days < 0) return { kind: 'lapsed', label: 'Lapsed', dateLabel }
  if (days <= 90) return { kind: 'expiring', label: `Expiring ${dateLabel}`, dateLabel }
  return { kind: 'valid', label: `Valid to ${dateLabel}`, dateLabel }
}

// Amber = the locked quiet-urgency semantic (sensitive/attention family).
// valid/unverified are deliberately NEUTRAL — valid is only "as last
// recorded", and unverified is not good standing, so neither gets green.
const ROATP_PILL: Record<RoatpKind, { bg: string; text: string; dot: string; border: string }> = {
  lapsed:     { bg: sensitive['banner-bg'], text: sensitive['banner-text'], dot: sensitive.indicator, border: sensitive['banner-border'] },
  expiring:   { bg: sensitive['row-tint'], text: sensitive['text-strong'], dot: sensitive.indicator, border: sensitive['banner-border'] },
  valid:      { bg: surface.panel, text: ink.secondary, dot: neutral.dot, border: surface.panel },
  unverified: { bg: surface.panel, text: ink.muted, dot: neutral.dot, border: surface.panel },
}

const AS_LAST_RECORDED = 'RoATP standing as last recorded — there is no live RoATP feed.'

function RoatpPill({ expiry }: { expiry?: string }) {
  const ind = roatpIndicator(expiry)
  const cfg = ROATP_PILL[ind.kind]
  const tip = ind.kind === 'lapsed' && ind.dateLabel
    ? `${AS_LAST_RECORDED} Expired ${ind.dateLabel}.`
    : AS_LAST_RECORDED
  return (
    <span title={tip} className="k-badge"
      style={{ background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}` }}>
      <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: cfg.dot }} />
      {ind.label.toUpperCase()}
    </span>
  )
}

// ── Status badge (active / inactive) ────────────────────────────────────────────

const STATUS_BADGE: Record<string, { bg: string; color: string }> = {
  active:   { bg: success.bg, color: success.text },
  inactive: { bg: surface.panel, color: ink.muted },
}

function ProviderStatusBadge({ status }: { status: string }) {
  const cfg = STATUS_BADGE[status] ?? { bg: surface.panel, color: ink.secondary }
  return (
    <span className="k-badge" style={{ background: cfg.bg, color: cfg.color }}>
      <span className="k-badge-dot" />{status.toUpperCase()}
    </span>
  )
}

// ── Standards sub-list (edit mode only — addStandard needs a provider id) ──────

function StandardsSection({ providerId }: { providerId: string }) {
  const [standards, setStandards] = useState<LearningProviderStandard[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [title, setTitle] = useState('')
  const [level, setLevel] = useState('')

  async function reload() {
    try {
      const s = await listStandards(providerId)
      setStandards(s)
      setLoadError(null)
    } catch (e) {
      // 404 = the PROVIDER is gone (a real error) — deliberately distinct
      // from a provider with no standards yet, which is an empty list.
      if (e instanceof ApiError && e.status === 404) setLoadError('This provider no longer exists.')
      else setLoadError(apiErrorMessage(e, 'Failed to load standards'))
    }
  }

  useEffect(() => {
    let alive = true
    listStandards(providerId)
      .then(s => { if (alive) { setStandards(s); setLoadError(null) } })
      .catch(e => {
        if (!alive) return
        if (e instanceof ApiError && e.status === 404) setLoadError('This provider no longer exists.')
        else setLoadError(apiErrorMessage(e, 'Failed to load standards'))
      })
    return () => { alive = false }
  }, [providerId])

  async function handleAdd() {
    if (!code.trim()) { toast.error('Standard code is required'); return }
    const lvl = Number(level)
    try {
      await addStandard(providerId, {
        standardCode: code.trim(),
        standardTitle: title.trim() || undefined,
        level: level.trim() && lvl > 0 ? lvl : undefined,
      })
      setCode(''); setTitle(''); setLevel('')
      toast.success('Standard added')
      await reload()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to add standard'))
    }
  }

  async function handleRemove(standardId: string) {
    try {
      await deleteStandard(providerId, standardId)
      setStandards(prev => prev ? prev.filter(s => s.id !== standardId) : prev)
      toast.success('Standard removed')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to remove standard'))
    }
  }

  const inputCls = "px-3 py-2 bg-surface-control border border-edge-control rounded-control text-12 text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent transition-all"

  return (
    <div className="pt-4 border-t border-edge-row">
      <div className="k-eyebrow uppercase mb-3">Standards delivered</div>
      {loadError ? (
        <div className="text-12 text-ink-muted">{loadError}</div>
      ) : standards === null ? (
        <div className="text-12 text-ink-muted">Loading standards…</div>
      ) : (
        <>
          {standards.length === 0 ? (
            <div className="text-12 text-ink-muted mb-3">No standards recorded for this provider yet.</div>
          ) : (
            <div className="flex flex-col mb-3">
              {standards.map(s => (
                <div key={s.id} className="flex items-center gap-3 py-2 border-b border-edge-row-faint last:border-b-0">
                  <span className="text-12 font-medium text-ink-primary">{s.standardCode}</span>
                  {s.level ? <span className="text-11 px-1.5 py-0.5 rounded-full bg-surface-panel text-ink-secondary">L{s.level}</span> : null}
                  <span className="text-12 text-ink-secondary truncate flex-1">{s.standardTitle || '—'}</span>
                  <button onClick={() => handleRemove(s.id)} title="Remove standard"
                    className="w-6 h-6 flex items-center justify-center rounded-control text-ink-faint hover:text-ink-primary hover:bg-surface-nav-hover transition-colors flex-shrink-0">
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <div className="w-28">
              <label className="block text-11 text-ink-muted mb-1">Code *</label>
              <input type="text" value={code} onChange={e => setCode(e.target.value)} placeholder="e.g. ST0001" className={`${inputCls} w-full`} />
            </div>
            <div className="flex-1">
              <label className="block text-11 text-ink-muted mb-1">Title</label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Adult Care Worker" className={`${inputCls} w-full`} />
            </div>
            <div className="w-16">
              <label className="block text-11 text-ink-muted mb-1">Level</label>
              <input type="number" min="1" max="8" value={level} onChange={e => setLevel(e.target.value)} placeholder="3" className={`${inputCls} w-full`} />
            </div>
            <button className="k-btn-secondary k-btn-secondary--flex" onClick={handleAdd}><Plus size={12} /> Add</button>
          </div>
        </>
      )}
    </div>
  )
}

// ── Create/edit drawer ──────────────────────────────────────────────────────────

function ProviderDrawer({ initial, onClose, onUpserted }: {
  /** null = create mode. */
  initial: LearningProvider | null
  onClose: () => void
  onUpserted: (p: LearningProvider) => void
}) {
  // After a create the drawer stays open in edit mode on the saved row, so
  // standards can be added straight away.
  const [saved, setSaved] = useState<LearningProvider | null>(initial)
  const [legalName, setLegalName] = useState(initial?.legalName ?? '')
  const [tradingName, setTradingName] = useState(initial?.tradingName ?? '')
  const [ukprn, setUkprn] = useState(initial?.ukprn ?? '')
  const [roatpStatus, setRoatpStatus] = useState(initial?.roatpStatus ?? '')
  const [roatpRoute, setRoatpRoute] = useState(initial?.roatpRoute ?? '')
  const [roatpExpiry, setRoatpExpiry] = useState(initial?.roatpExpiry?.slice(0, 10) ?? '')
  const [contactName, setContactName] = useState(initial?.contactName ?? '')
  const [contactEmail, setContactEmail] = useState(initial?.contactEmail ?? '')
  const [contactPhone, setContactPhone] = useState(initial?.contactPhone ?? '')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  // The recorded expiry cannot be cleared: update is partial (empty =
  // unchanged) — there is no clear-to-null until OptionalString is adopted on
  // roatp_expiry (the stage-1 follow-up). Surface that instead of faking it.
  const hasRecordedExpiry = Boolean(saved?.roatpExpiry)
  const savedExpiryLabel = saved?.roatpExpiry ? roatpIndicator(saved.roatpExpiry).dateLabel : undefined

  async function handleSave() {
    if (!legalName.trim()) { toast.error('Legal name is required'); return }
    setBusy(true)
    try {
      const body = {
        legalName: legalName.trim(),
        tradingName: tradingName.trim() || undefined,
        // UKPRN stays a string end to end — leading zeros are significant.
        ukprn: ukprn.trim() || undefined,
        roatpStatus: roatpStatus.trim() || undefined,
        roatpRoute: roatpRoute.trim() || undefined,
        roatpExpiry: roatpExpiry || undefined,
        contactName: contactName.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
      }
      const p = saved ? await updateProvider(saved.id, body) : await createProvider(body)
      setSaved(p)
      onUpserted(p)
      toast.success(saved ? 'Provider updated' : 'Provider created — you can now add its standards')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to save provider'))
    } finally {
      setBusy(false)
    }
  }

  async function handleStatusToggle(active: boolean) {
    if (!saved) return
    try {
      const p = await setProviderStatus(saved.id, active ? 'active' : 'inactive')
      setSaved(p)
      onUpserted(p)
      toast.success(active ? 'Provider set active' : 'Provider set inactive')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to change status'))
    }
  }

  const inputCls = "w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint transition-all"
  const labelCls = "block text-12 font-semibold text-ink-body mb-2"

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(560px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">{saved ? saved.legalName : 'New Provider'}</div>
            <div className="text-12 text-ink-muted mt-1">
              {saved ? 'Edit learning provider' : 'Add an apprenticeship training provider'}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {saved && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-edge-row flex-shrink-0">
            <div className="flex items-center gap-3">
              <ProviderStatusBadge status={saved.status} />
              <RoatpPill expiry={saved.roatpExpiry} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-12 text-ink-secondary">Active</span>
              <Toggle checked={saved.status === 'active'} onChange={handleStatusToggle} />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <div>
            <label className={labelCls}>Legal Name *</label>
            <input type="text" value={legalName} onChange={e => setLegalName(e.target.value)} placeholder="e.g. Northern Training Group Ltd" autoFocus={!saved} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Trading Name</label>
              <input type="text" value={tradingName} onChange={e => setTradingName(e.target.value)} placeholder="e.g. NTG Apprenticeships" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>UKPRN</label>
              {/* type=text on purpose: UKPRN leading zeros are significant. */}
              <input type="text" inputMode="numeric" value={ukprn} onChange={e => setUkprn(e.target.value)} placeholder="e.g. 10012345" className={inputCls} />
            </div>
          </div>

          <div className="pt-4 border-t border-edge-row">
            <div className="k-eyebrow uppercase">RoATP</div>
            <div className="text-12 text-ink-muted mt-1 mb-4">As last recorded — there is no live RoATP feed.</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>RoATP Status</label>
                <input type="text" value={roatpStatus} onChange={e => setRoatpStatus(e.target.value)} placeholder="e.g. main" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>RoATP Route</label>
                <input type="text" value={roatpRoute} onChange={e => setRoatpRoute(e.target.value)} placeholder="e.g. apprenticeships" className={inputCls} />
              </div>
            </div>
            <div className="mt-4">
              <label className={labelCls}>RoATP Expiry</label>
              <input type="date" value={roatpExpiry} onChange={e => setRoatpExpiry(e.target.value)} className={inputCls} />
              {hasRecordedExpiry && (
                <div className="text-11 text-ink-muted mt-2">
                  Clearing a recorded expiry isn't supported yet — leaving this empty keeps {savedExpiryLabel ?? 'the recorded date'}.
                </div>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-edge-row">
            <div className="k-eyebrow uppercase mb-4">Contact</div>
            <div className="flex flex-col gap-4">
              <div>
                <label className={labelCls}>Contact Name</label>
                <input type="text" value={contactName} onChange={e => setContactName(e.target.value)} placeholder="e.g. Sam Whittaker" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="sam@provider.co.uk" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Phone</label>
                  <input type="tel" value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="+44 …" className={inputCls} />
                </div>
              </div>
            </div>
          </div>

          {saved ? (
            <StandardsSection providerId={saved.id} />
          ) : (
            <div className="pt-4 border-t border-edge-row">
              <div className="k-eyebrow uppercase">Standards delivered</div>
              <div className="text-12 text-ink-muted mt-1">Save the provider first to add the standards it delivers.</div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <button onClick={onClose} className="k-btn-secondary">
            Close
          </button>
          <button onClick={handleSave} disabled={busy}
            className="k-btn k-btn--lg disabled:opacity-60">
            {saved ? 'Save Changes' : 'Create Provider'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

const STATUS_FILTERS = ['all', 'active', 'inactive'] as const

export default function HRProviders() {
  const { data, loading, error } = usePolledAsync(() => listProviders(), 10_000, [])
  const [providers, setProviders] = useState<LearningProvider[]>([])
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>('all')
  const [search, setSearch] = useState('')
  const [drawer, setDrawer] = useState<{ initial: LearningProvider | null } | null>(null)

  useEffect(() => { if (data) setProviders(data) }, [data])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const filtered = providers.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false
    if (search) {
      const q = search.toLowerCase()
      if (!p.legalName.toLowerCase().includes(q) &&
          !(p.tradingName ?? '').toLowerCase().includes(q) &&
          !(p.ukprn ?? '').includes(search.trim())) return false
    }
    return true
  })

  const active = providers.filter(p => p.status === 'active').length
  const kinds = providers.map(p => roatpIndicator(p.roatpExpiry).kind)
  const needsAttention = kinds.filter(k => k === 'lapsed' || k === 'expiring').length
  const unverified = kinds.filter(k => k === 'unverified').length

  function upsert(p: LearningProvider) {
    setProviders(prev => prev.some(x => x.id === p.id) ? prev.map(x => x.id === p.id ? p : x) : [p, ...prev])
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Learning Providers"
        subtitle={`${filtered.length} provider${filtered.length !== 1 ? 's' : ''}`}
        action={<button className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => setDrawer({ initial: null })}><Plus size={14} /> New Provider</button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <HRMetric label="Providers" value={providers.length} />
        <HRMetric label="Active" value={active} />
        <HRMetric label="RoATP attention" value={needsAttention}
          tone={needsAttention > 0 ? 'amber' : 'default'}
          note="lapsed or expiring within 90 days — as last recorded" />
        <HRMetric label="Unverified" value={unverified}
          note="no RoATP expiry recorded" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_FILTERS.map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-2 rounded-full text-12 font-medium transition-colors capitalize ${statusFilter === s ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control hover:border-edge-control-hover'}`}>
            {s === 'all' ? 'All' : s}
          </button>
        ))}
        <div className="ml-auto">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name, UKPRN…"
            className="h-8 px-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent w-56" />
        </div>
      </div>

      <div className="k-card">
        {filtered.length === 0 ? (
          <EmptyState message="No providers match your filter"
            hint="Providers anchor apprenticeship funding records."
            actionLabel="New Provider" onAction={() => setDrawer({ initial: null })} />
        ) : (
          <table className="closr-table hr-table w-full">
            <thead>
              <tr>
                <th className="text-left">Provider</th>
                <th className="text-left">UKPRN</th>
                <th className="text-left">Status</th>
                <th className="text-left">RoATP</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="cursor-pointer" onClick={() => setDrawer({ initial: p })}>
                  <td>
                    <div className="text-13 font-medium text-ink-primary">{p.legalName}</div>
                    {p.tradingName && <div className="text-12 text-ink-muted">t/a {p.tradingName}</div>}
                  </td>
                  <td className="text-12 text-ink-secondary">{p.ukprn || '—'}</td>
                  <td><ProviderStatusBadge status={p.status} /></td>
                  <td><RoatpPill expiry={p.roatpExpiry} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {drawer && (
        <ProviderDrawer
          key={drawer.initial?.id ?? 'new'}
          initial={drawer.initial}
          onClose={() => setDrawer(null)}
          onUpserted={upsert}
        />
      )}
    </div>
  )
}
